import { useEffect } from 'react';
import { useGameStore, useKnockRequests, useLocalBubbleId, useLockedBubbles, EventBus } from '@campus/core';
import { useIsMobile } from '../hooks/useIsMobile.js';
import { Button, ControlButton, Icon } from '../ds/index.js';
import { jwtRole } from '../../lib/jwtRole.js';

/**
 * KnockNotification — quelqu'un toque à la porte de la salle où tu es.
 * Visible uniquement par les occupants de la bulle. Faire entrer / Refuser,
 * et raccourci pour ouvrir la salle à tout le monde.
 */
export function KnockNotification() {
  const { knockRequests, removeKnockRequest } = useKnockRequests();
  const bubbleNames = useGameStore((s) => s.bubbleNames);
  const lockedBubbles = useLockedBubbles();
  const isMobile = useIsMobile();

  // Auto-remove après 30s
  useEffect(() => {
    if (knockRequests.length === 0) return;
    const timers = knockRequests.map((r) =>
      setTimeout(() => removeKnockRequest(r.userId), 30_000),
    );
    return () => timers.forEach(clearTimeout);
  }, [knockRequests, removeKnockRequest]);

  if (knockRequests.length === 0) return null;

  const respond = (req: { userId: string; bubbleId: string }, granted: boolean) =>
    EventBus.emit('ui:knock-respond', { userId: req.userId, bubbleId: req.bubbleId, granted });

  return (
    <div style={{ width: isMobile ? undefined : 340, maxWidth: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {knockRequests.map((req) => {
        const room = bubbleNames.get(req.bubbleId) ?? 'la salle';
        const locked = lockedBubbles.has(req.bubbleId);
        return (
          <div key={req.userId} className="pl-pop" role="alertdialog" aria-label="Quelqu'un toque à la porte" style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span aria-hidden="true" style={{ flexShrink: 0, width: 32, height: 32, borderRadius: 'var(--radius-full)', background: 'var(--color-bg-raised)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', font: '800 13px/1 var(--font-display)' }}>
                {(req.displayName[0] ?? '?').toUpperCase()}
              </span>
              <p style={{ margin: 0, flex: 1, fontSize: 13, lineHeight: 1.45 }}>
                <strong>{req.displayName}</strong> toque à la porte de <strong>{room}</strong>.
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant={isMobile ? 'secondary' : 'ghost'} size="sm" onClick={() => respond(req, false)} style={isMobile ? { flex: 1, height: 44 } : undefined}>Refuser</Button>
              <Button variant="primary" size="sm" onClick={() => respond(req, true)} style={isMobile ? { flex: 1, height: 44 } : undefined}>Faire entrer</Button>
            </div>
            {locked && !isMobile && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 10, borderTop: '1px solid var(--color-border-default)', fontSize: 12, color: 'var(--color-text-secondary)' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Icon name="lock" size={14} />Salle fermée</span>
                <Button variant="ghost" size="sm" onClick={() => EventBus.emit('ui:lock-bubble', { bubbleId: req.bubbleId, locked: false })}>Ouvrir la salle</Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * LockButton — Bouton cadenas dans le HUD quand le joueur est dans une bulle.
 */
export function LockButton() {
  const localBubbleId = useLocalBubbleId();
  const lockedBubbles = useLockedBubbles();
  const role = useGameStore((s) => jwtRole(s.jwt))?.toUpperCase();
  const myDeskId = useGameStore((s) => s.personalDeskUi.myDesk?.id);

  // Toute bulle se ferme : salle avec porte (porte verrouillée), ou salon
  // ouvert (mur invisible et « Toquer » à son bord, ZoneScene.syncOpenLockBarriers).
  // Réservé aux OWNER/ADMIN, et au propriétaire sur son bureau personnel
  // (même règle que ZoneRoom, qui décide).
  if (!localBubbleId) return null;
  const isAdmin = role === 'OWNER' || role === 'ADMIN';
  const ownDesk = !!myDeskId && localBubbleId === `desk:${myDeskId}`;
  if (!isAdmin && !ownDesk) return null;

  const isLocked = lockedBubbles.has(localBubbleId);

  return (
    <ControlButton
      icon={isLocked ? 'lock' : 'unlock'}
      label={isLocked ? 'Fermée' : 'Ouverte'}
      active={isLocked}
      title={isLocked ? 'Ouvrir la salle' : 'Fermer la salle'}
      onClick={() => EventBus.emit('ui:lock-bubble', { bubbleId: localBubbleId, locked: !isLocked })}
    />
  );
}

